import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { FacilityGetModel } from "./Facilities";

/**
 * ### 他事業所連携 API 操作クラス
 */
export default class OtherOfficeConnections {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeConnections
   */
  public async gets(userStore: LoginUser): Promise<Array<OtherOfficeConnectionGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeConnections, formatParams);

    const response = await this.commonService.executeGet<Array<OtherOfficeConnectionGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 3);

    return response.data;
  }

  /**
   * ### POST OtherOfficeConnectionMail
   */
  public async sendMail(userStore: LoginUser, requestBody: OtherOfficeConnectionMailPostModel): Promise<OtherOfficeConnectionGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeConnectionMail, formatParams);

    const response = await this.commonService.executePost<OtherOfficeConnectionGetModel>(
      userStore,
      requestUrl,
      requestBody,
      this.constructor.name,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * PATCH OtherOfficeConnections
   */
  public async patch(userStore: LoginUser, requestBody: OtherOfficeConnectionPatchModel): Promise<OtherOfficeConnectionGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeConnections, formatParams);

    const response = await this.commonService.executePatch<OtherOfficeConnectionGetModel>(
      userStore,
      requestUrl,
      requestBody,
      this.constructor.name,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * DELETE OtherOfficeConnections
   */
  public async delete(userStore: LoginUser, requestBody: OtherOfficeConnectionDeleteModel): Promise<OtherOfficeConnectionGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeConnections, formatParams);

    const response = await this.commonService.executeDelete<OtherOfficeConnectionGetModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0,
      requestBody
    );

    return response.data;
  }
}

export interface OtherOfficeConnectionGetModel {
  /** 施設ID（招待元施設のID） */
  facilityId: number;
  /** 招待メール送信先メールアドレス */
  mailaddress: string;
  /** 招待した側か否か */
  isSender: boolean;
  /** 招待され連携登録されたか否か（登録済み：true、未登録：false） */
  registerd: boolean;
  /** 登録された施設ID。registerd=trueにするタイミングで格納される */
  registerdFacilityId: number;
  /** 事業所名 */
  officeName: string;
  /** 担当者名 */
  staffName: string;
  /** ブロックされた（相手側施設から削除された）否か */
  blocked: boolean;
  /** 最終招待送信日時 */
  latestSendDatetime: number;

  /** 施設情報。連携登録済みの場合のみ存在する */
  facility: FacilityGetModel;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface OtherOfficeConnectionMailPostModel {
  /** 送信メールアドレス */
  mailaddress: string;
  /** 事業所名 */
  officeName: string;
  /** 担当者名 */
  staffName: string;
  /** 送信種別 */
  sendType: SenderType;
}

export enum SenderType {
  NEW = "new",
  RESEND = "resend"
}

export interface OtherOfficeConnectionPatchModel {
  /** 送信メールアドレス */
  mailaddress: string;
  /** 事業所名 */
  officeName: string;
  /** 担当者 */
  staffName: string;
  /** 更新日時 */
  updated: number;
}

export interface OtherOfficeConnectionDeleteModel {
  /** 送信メールアドレス */
  mailaddress: string;
}
